import { useMemo, useState } from 'react';

import { SurfaceDataModel } from '../A2uiSurface';
import { PulseFormBindings, readPulseFormSelection } from './pulseFormBindings';
import { PulsePeopleCount, PulseSelection, usePulsePeopleCount } from './usePulsePeopleCount';

interface PulseSurfacePeopleCountArgs {
  /** Where the surface keeps its topics and geo filters; null when it isn't a Pulse surface. */
  bindings: PulseFormBindings | null;
  /**
   * The selection before the user edits anything, and the filters counted when the surface has no
   * geo filter field. Null while it is still loading. Omitted, the count waits for the data model.
   */
  baseline?: PulseSelection | null;
  /** False on submitted or stale surfaces (see usePulsePeopleCount). */
  active: boolean;
}

interface PulseSurfacePeopleCount {
  peopleCount: PulsePeopleCount | null;
  /** For A2uiSurface's `onDataModelChange`; undefined when there is nothing to observe. */
  onDataModelChange: ((dataModel: SurfaceDataModel) => void) | undefined;
}

/**
 * Live people count of a Pulse surface (an agent-composed form or a confirmation card), from what
 * the user has entered so far. Until the surface reports its data model, the baseline is counted.
 */
export const usePulseSurfacePeopleCount = ({
  bindings,
  baseline,
  active,
}: PulseSurfacePeopleCountArgs): PulseSurfacePeopleCount => {
  const [dataModel, setDataModel] = useState<SurfaceDataModel | null>(null);
  const selection = useMemo(
    () => readSurfaceSelection({ bindings, baseline, dataModel }),
    [bindings, baseline, dataModel],
  );
  const peopleCount = usePulsePeopleCount({ selection, enabled: !!bindings, active });
  return { peopleCount, onDataModelChange: bindings ? setDataModel : undefined };
};

interface SurfaceSelectionArgs {
  bindings: PulseFormBindings | null;
  baseline: PulseSelection | null | undefined;
  dataModel: SurfaceDataModel | null;
}

// The edited topics and geo filters where the surface has a field for them, the baseline's
// otherwise; null until the selection is known.
const readSurfaceSelection = ({ bindings, baseline, dataModel }: SurfaceSelectionArgs): PulseSelection | null => {
  if (!bindings || baseline === null) return null;
  if (!dataModel) return baseline ?? null;
  const edited = readPulseFormSelection(bindings, dataModel);
  if (!baseline) return edited;
  return {
    topicIds: bindings.topicsPath ? edited.topicIds : baseline.topicIds,
    filters: bindings.geoFilterPaths.length ? edited.filters : baseline.filters,
  };
};
